/**
 * Экран «Администратор» (задача 23) — раздел помощника организатора: секции
 * «Турнир» (название, город и сроки — те же значения показывает чекбокс подтверждения
 * участия в заявке), «Действия» (выгрузка всех заявок файлом JSON, выгрузка сканов
 * участников — задача 41: архивами по командам, чтобы комплект документов
 * был доступен офлайн на мероприятии, — и обнуление подтверждений всех заявок) и
 * «Пользователи»
 * (назначение ролей «Организатор» и «Администратор» пользователям реестра и удаление
 * аккаунта вместе со всеми его данными).
 *
 * Экран открыт только роли administrator: права проверяет API (`withRoles`), а адрес
 * `#/admin` показывается лишь тем, у кого роль есть (`main.ts`). Данные — одна загрузка
 * (`GET /api/admin/screen`): описание турнира, список пользователей и журнал аудита за
 * сутки (задача 38, этап C — журнал не стоит отдельного запуска функции); сохранение —
 * отдельными запросами по секциям. Общей кнопки «Сохранить» нет: у секций
 * автосохранение, как у секций заявки (общие примитивы — src/frontend/ui.ts).
 */
import { ASSIGNABLE_ROLES, ROLE_TITLES } from '../shared/roles';
import type { AdminUser, EventInfo, PublicUser, UserRole } from '../shared/types';
import * as api from './api';
import { ApiError } from './api';
import { userMasthead } from './accountMenu';
import { auditSection } from './adminAudit';
import { button, el, iconButton, messageBox, renderScreen } from './dom';
import {
  formatBytes,
  localStorageMemory,
  runDocumentsExport,
  type ExportProgress,
  type ExportResult,
} from './documentsExport';
import { documentsDownloadSink } from './fileSink';
import {
  cell,
  checkbox,
  confirmDialog,
  errorText,
  fieldsSection,
  saveStatus,
  sectionFrame,
  showLoadError,
  tableHead,
  textControl,
  type SectionField,
} from './ui';

/** Контекст экрана: общий сбой (истёкшая сессия, сбой загрузки). */
interface ScreenContext {
  fail(err: unknown): void;
}

/**
 * Признак строки таблицы «Пользователи», которым правит флажок, — ключ поля `AdminUser`.
 * Оба признака меняет один запрос: состояние строки на экране одно (см. `toggleFlag`).
 */
type UserFlagField = 'approved' | 'active';

/** Подписи колонок-признаков: те же строки идут в подписи флажков (aria-label). */
const USER_FLAG_TITLES: Record<UserFlagField, string> = {
  approved: 'Подтверждённый',
  active: 'Активный',
};

/**
 * Подписи колонок таблицы «Пользователи» — по одной на колонку, порядок как на экране:
 * данные пользователя, признаки аккаунта (подтверждение администратором и активность),
 * роли и действия строки. Последняя колонка — действия (удаление аккаунта): заголовок
 * нужен широкой раскладке, а в карточке он скрыт (модификатор `.table--no-actions-label`).
 */
const USER_COLUMNS = [
  'Фамилия, имя',
  'Логин',
  USER_FLAG_TITLES.approved,
  USER_FLAG_TITLES.active,
  ROLE_TITLES.user,
  ROLE_TITLES.organizer,
  ROLE_TITLES.administrator,
  'Действия',
];

/** Вопрос диалога подтверждения в секции «Действия» (сценарий 7 docs/spec.md). */
const RESET_QUESTION = 'Данное действие сбросит все подтверждения всех заявок. Продолжаем?';

/** Поля секции «Турнир» в порядке отображения (порядок задаёт и `fromValues`). */
const TOURNAMENT_FIELDS: SectionField<EventInfo>[] = [
  {
    id: 'tournament-name',
    label: 'Название турнира',
    view: (row) => row.name,
    control: (row) => textControl(row.name),
  },
  {
    id: 'tournament-city',
    label: 'Город',
    view: (row) => row.city,
    control: (row) => textControl(row.city),
  },
  {
    id: 'tournament-dates',
    label: 'Сроки',
    view: (row) => row.dates,
    control: (row) => textControl(row.dates),
  },
];

/** Открывает экран «Администратор»: загружает турнир, пользователей и журнал, рисует секции. */
export function showAdminScreen(user: PublicUser): void {
  const message = messageBox();
  const sections = el('div', { className: 'application' });
  renderScreen(
    'Администратор',
    el('div', { className: 'application-screen', children: [message.root, sections] }),
    userMasthead(user),
  );
  const context: ScreenContext = { fail: (err) => showLoadError(err, message) };

  // Секция «Аудит» создаётся сразу, до ответа экрана: её данные приходят вместе с экраном
  // (задача 38, этап C), поэтому журнал не прибавляется к времени открытия раздела
  // отдельным запросом к функции и не ждёт ответа первого запроса.
  const audit = auditSection(context);

  message.show('Загружаем раздел…', 'info');
  api
    .getAdminScreen()
    .then((screen) => {
      message.clear();
      sections.replaceChildren(
        tournamentSection(screen.event, context),
        actionsSection(screen.event, context),
        usersSection(screen.users, context),
        // Журнал аудита (задача 37): только чтение, период — «за день» по умолчанию.
        audit.root,
      );
      audit.showFromScreen(screen.audit);
    })
    .catch((err: unknown) => showLoadError(err, message));
}

/**
 * Секция «Турнир»: название, город и сроки с автосохранением — оформлена как секция
 * «Организация» на экране заявки (просмотр, «карандаш», индикатор сохранения).
 * Значения те же, что видит тренер в подписи чекбокса подтверждения участия.
 */
function tournamentSection(initial: EventInfo, context: ScreenContext): HTMLElement {
  /** Последнее сохранённое состояние — источник правды для режима просмотра. */
  let current = initial;

  return fieldsSection<EventInfo>({
    title: 'Турнир',
    editTitle: 'Редактировать турнир',
    fields: TOURNAMENT_FIELDS,
    data: () => current,
    // Поля идут в порядке TOURNAMENT_FIELDS: позиция значения = позиция поля.
    fromValues: (values) => ({ name: values[0], city: values[1], dates: values[2] }),
    save: async (row) => {
      current = (await api.saveTournament(row)).event;
    },
    fail: context.fail,
  });
}

/**
 * Секция «Действия»: операции администратора над заявками целиком.
 *
 * - «Скачать все заявки» — все заявки одним JSON-файлом (сценарий 5 docs/spec.md, формат —
 *   docs/integration/access-export.md);
 * - «Скачать документы (ZIP по командам)» — выгрузка сканов участников, чтобы на мероприятии
 *   комплект документов был доступен офлайн (задача 41): файлы забираются по подписанным
 *   ссылкам, а браузер собирает архив на каждую команду `<команда>.zip`;
 * - «Сбросить подтверждения» — красная кнопка с диалогом подтверждения («Да»/«Нет»).
 *
 * Пока операция в пути, кнопки и флажок выключены: случайной второй выгрузки или второго
 * сброса не будет. У выгрузки документов дополнительно появляется «Остановить»: она идёт
 * минутами, её прерывают по необходимости, а уже выгруженные команды остаются на диске.
 * Кнопки стоят в панели действий, поэтому тело секции получает верхний отступ, равный
 * сумме нижних (метка `section--actions`, styles/components.css).
 */
function actionsSection(event: EventInfo, context: ScreenContext): HTMLElement {
  const message = messageBox();
  const body = el('div', { className: 'section__body' });
  const { root } = sectionFrame('Действия', body, { sectionClass: 'section--actions' });

  /** Запрошена ли остановка выгрузки документов (кнопка «Остановить»). */
  let cancelRequested = false;

  const exportButton = button({
    text: 'Скачать все заявки',
    onClick: () => void exportApplications(),
  });
  const zipButton = button({
    text: 'Скачать документы (ZIP по командам)',
    onClick: () => void exportDocuments(),
  });
  const stopButton = button({
    text: 'Остановить выгрузку',
    variant: 'danger',
    onClick: () => {
      cancelRequested = true;
      message.show('Останавливаем выгрузку: команда в работе будет перекачана позже.', 'info');
    },
  });
  // «Остановить» показывается только во время выгрузки документов: остальные операции
  // заканчиваются за секунды и прерывать их нечем.
  stopButton.hidden = true;
  const freshCheckbox = checkbox({
    checked: false,
    label: 'Выгрузка документов: скачивать заново, не пропуская уже выгруженные команды',
  });
  freshCheckbox.id = 'documents-export-fresh';
  const resetButton = button({
    text: 'Сбросить подтверждения',
    variant: 'danger',
    onClick: () => void reset(),
  });

  /** Элементы секции, которые выключаются, пока операция в пути (кроме «Остановить»). */
  function sectionControls(): (HTMLButtonElement | HTMLInputElement)[] {
    return [exportButton, zipButton, resetButton, freshCheckbox];
  }

  /**
   * Выполняет операцию секции: пока запрос в пути, элементы выключены. `null` из
   * операции означает отказ от неё (диалог ответил «Нет») — сообщение не меняется.
   */
  async function run(operation: () => Promise<string | null>): Promise<void> {
    for (const control of sectionControls()) control.disabled = true;
    try {
      const text = await operation();
      if (text !== null) message.show(text, 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        context.fail(err);
        return;
      }
      message.show(errorText(err), 'error');
    } finally {
      for (const control of sectionControls()) control.disabled = false;
      stopButton.hidden = true;
      cancelRequested = false;
    }
  }

  /** Выгрузка всех заявок файлом JSON: сообщаем, сколько заявок в файле. */
  function exportApplications(): Promise<void> {
    return run(async () => {
      message.show('Готовим выгрузку заявок…', 'info');
      const count = await api.downloadApplicationsExport();
      return count === 0
        ? 'Готово: заявок в системе нет — в файле пустой список.'
        : `Готово: заявок в файле — ${count}.`;
    });
  }

  /**
   * Выгрузка документов участников (задача 41): браузер собирает архив `<команда>.zip` на
   * каждую команду и отдаёт его системным скачиванием. Выбранной папки больше нет — Chrome
   * отклонял выбор каталога, а одного режима ZIP достаточно (правка 23.10.2026).
   */
  function exportDocuments(): Promise<void> {
    return run(async () => {
      message.show('Загружаем список документов…', 'info');
      stopButton.hidden = false;
      const result = await runDocumentsExport({
        manifest: () => api.downloadDocumentsManifest(),
        file: (ref) => api.downloadAdminDocumentFile(ref),
        sink: documentsDownloadSink,
        // Память выгрузки привязана к мероприятию: команды следующего турнира не считаются
        // выгруженными, а флажок «скачивать заново» её полностью игнорирует.
        memory: localStorageMemory(event.name, !freshCheckbox.checked),
        progress: (state) => message.show(progressText(state), 'info'),
        isCancelled: () => cancelRequested,
      });
      return summaryText(result);
    });
  }

  /** Сброс подтверждений: диалог подтверждения, затем число заявок со снятым признаком. */
  function reset(): Promise<void> {
    return run(async () => {
      if (!(await confirmDialog(RESET_QUESTION, 'Да', 'Нет'))) return null;

      message.show('Сбрасываем подтверждения…', 'info');
      const { resetCount } = await api.resetConfirmations();
      return `Готово: подтверждения сняты у ${resetCount} заявок.`;
    });
  }

  body.replaceChildren(
    el('div', {
      className: 'toolbar',
      // «Остановить выгрузку» стоит сразу за «Скачать документы»: она относится именно
      // к выгрузке документов и показывается только пока та активна.
      children: [exportButton, zipButton, stopButton, resetButton],
    }),
    el('label', {
      children: [
        freshCheckbox,
        el('span', {
          text: ' выгрузка документов — скачивать заново (не пропускать уже выгруженные команды)',
        }),
      ],
    }),
    message.root,
  );
  return root;
}

/** Прогресс выгрузки документов: команды, файлы, объём и текущая команда. */
function progressText(state: ExportProgress): string {
  const current = state.currentTeam === '' ? '' : `, команда «${state.currentTeam}»`;
  return (
    `Собираем архивы команд: команд — ${state.teamsDone} из ${state.teamsTotal}, ` +
    `файлов — ${state.filesDone} из ${state.filesTotal} ` +
    `(${formatBytes(state.bytesDone)} из ${formatBytes(state.bytesTotal)})${current}.`
  );
}

/** Итог выгрузки документов: сколько скачано, что пропущено и что не удалось. */
function summaryText(result: ExportResult): string {
  const parts: string[] = [];

  if (result.cancelled) {
    parts.push(
      `Выгрузка остановлена: команд — ${result.teamsDone} из ${result.teamsTotal}, ` +
        `файлов — ${result.filesDone}. Повторный запуск продолжит с того же места.`,
    );
  } else if (result.teamsTotal === 0) {
    parts.push('Подтверждённых заявок нет: выгружать нечего.');
  } else {
    parts.push(
      `Готово: команд — ${result.teamsDone}, файлов — ${result.filesDone} ` +
        `(${formatBytes(result.bytesDone)}). Комплектность — в файле «_состав.csv».`,
    );
  }
  if (result.skippedTeams > 0) {
    parts.push(`Пропущено уже выгруженных команд — ${result.skippedTeams}.`);
  }
  if (result.failures.length > 0) {
    parts.push(
      `Не удалось скачать файлов — ${result.failures.length}: список в файле «_ошибки.txt».`,
    );
  }
  return parts.join(' ');
}

/**
 * Секция «Пользователи»: таблица реестра. Колонки «Фамилия, имя» и «Логин» — только
 * показ; «Подтверждённый» (подтверждение администратором) и «Активный» (право входа) —
 * чекбоксы признаков, изменение уходит на сервер сразу; «Пользователь» — отметка без
 * правки (базовая роль есть у любого аккаунта), «Организатор» и «Администратор» —
 * чекбоксы ролей: отметка назначает роль, снятие забирает. Последняя колонка — корзина:
 * удаление пользователя вместе со всеми его данными (заявкой, документами, ролями),
 * после подтверждения диалогом.
 */
function usersSection(initial: AdminUser[], context: ScreenContext): HTMLElement {
  const body = el('div', { className: 'section__body' });
  const { root, indicator } = sectionFrame('Пользователи', body);
  const status = saveStatus(context.fail, indicator);
  /** Последнее состояние таблицы: источник правды для чекбоксов. */
  let users = initial;

  /** Перерисовывает таблицу целиком: строки по отдельности не редактируются. */
  function render(): void {
    body.replaceChildren(
      el('table', {
        // Подпись «Действия» в карточке скрыта: в ячейке стоит только корзина.
        className: 'table table--roles table--no-actions-label',
        children: [
          tableHead(USER_COLUMNS),
          el('tbody', { children: users.map((user) => tableRow(user)) }),
        ],
      }),
    );
  }

  /**
   * Строка таблицы: данные пользователя, флажки признаков и ролей, корзина удаления.
   * Ячейки собираются парами «подпись — содержимое» в порядке колонок USER_COLUMNS:
   * подпись нужна карточной раскладке, и пары избавляют от нумерации колонок по индексам.
   */
  function tableRow(user: AdminUser): HTMLTableRowElement {
    const cells: [string, Node][] = [
      [USER_COLUMNS[0], el('span', { text: fullName(user) })],
      [USER_COLUMNS[1], el('span', { text: user.email })],
      [USER_COLUMNS[2], flagCheckbox(user, 'approved')],
      [USER_COLUMNS[3], flagCheckbox(user, 'active')],
      [USER_COLUMNS[4], roleCheckbox(user, 'user')],
      [USER_COLUMNS[5], roleCheckbox(user, 'organizer')],
      [USER_COLUMNS[6], roleCheckbox(user, 'administrator')],
      [
        USER_COLUMNS[7],
        el('div', {
          className: 'row-view__actions',
          children: [
            iconButton({
              icon: 'trash',
              variant: 'danger',
              title: `Удалить пользователя «${fullName(user)}»`,
              onClick: () => void removeUser(user),
            }),
          ],
        }),
      ],
    ];

    return el('tr', { children: cells.map(([label, node]) => cell(label, node)) });
  }

  /**
   * Чекбокс признака: «Подтверждённый» — подтверждение администратором (заявка
   * неподтверждённого не учитывается в таблицах раздела организатора), «Активный» —
   * право входа в сервис.
   */
  function flagCheckbox(user: AdminUser, field: UserFlagField): HTMLInputElement {
    return checkbox({
      checked: user[field],
      label: `${USER_FLAG_TITLES[field]} — ${fullName(user)}`,
      onToggle: (value) => void toggleFlag(user, field, value),
    });
  }

  /**
   * Чекбокс роли. «Пользователь» — только отметка (роль есть у любого аккаунта, поле
   * read-only), «Организатор» и «Администратор» отправляют изменение на сервер.
   */
  function roleCheckbox(user: AdminUser, role: UserRole): HTMLInputElement {
    const readonly = role === 'user';
    return checkbox({
      checked: user.roles.includes(role),
      label: `${ROLE_TITLES[role]} — ${fullName(user)}`,
      disabled: readonly,
      onToggle: readonly ? undefined : (assigned) => void toggleRole(user, role, assigned),
    });
  }

  /**
   * Меняет признак строки и перерисовывает таблицу по ответу сервера. Оба признака
   * уходят одним запросом: состояние строки на экране одно, и сервер ждёт оба значения.
   */
  async function toggleFlag(user: AdminUser, field: UserFlagField, value: boolean): Promise<void> {
    const next = { approved: user.approved, active: user.active, [field]: value };

    status.pending();
    try {
      const updated = (await api.setUserFlags(user.id, next)).user;
      users = users.map((item) => (item.id === updated.id ? updated : item));
      status.saved();
    } catch (err) {
      status.error(err);
    }
    // Перерисовка синхронизирует флажки с состоянием сервера: неудачная правка
    // (например, попытка выключить свой аккаунт — 400) в интерфейсе не остаётся.
    render();
  }

  /** Назначает или снимает роль и перерисовывает таблицу по ответу сервера. */
  async function toggleRole(user: AdminUser, role: UserRole, assigned: boolean): Promise<void> {
    const next = assigned ? [...user.roles, role] : user.roles.filter((item) => item !== role);

    status.pending();
    try {
      const updated = (await api.setUserRoles(user.id, assignable(next))).user;
      users = users.map((item) => (item.id === updated.id ? updated : item));
      status.saved();
    } catch (err) {
      status.error(err);
    }
    // Перерисовка синхронизирует чекбоксы с состоянием сервера: неудачная правка
    // (например, отказ снять роль администратора у себя) в интерфейсе не остаётся.
    render();
  }

  /**
   * Удаляет пользователя и все его данные после подтверждения диалогом. Состоянием
   * таблицы становится ответ сервера (оставшийся список) — как после смены ролей; при
   * ошибке (например, попытка удалить себя — 400) строка остаётся на месте, а причина
   * видна в индикаторе секции.
   */
  async function removeUser(user: AdminUser): Promise<void> {
    if (!(await confirmDialog(deleteQuestion(user), 'Удалить'))) return;

    status.pending();
    try {
      users = (await api.deleteUser(user.id)).users;
      status.saved();
    } catch (err) {
      status.error(err);
    }
    render();
  }

  render();
  return root;
}

/** Вопрос диалога удаления: перечисляем, что исчезнет вместе с аккаунтом. */
function deleteQuestion(user: AdminUser): string {
  return (
    `Удалить пользователя «${fullName(user)}»? Вместе с ним будут удалены его заявка, ` +
    'документы и назначенные роли — восстановить их нельзя.'
  );
}

/** Фамилия и имя одной строкой: у пользователя реестра оба поля обязательные. */
function fullName(user: AdminUser): string {
  return `${user.lastName} ${user.firstName}`.trim();
}

/** Роли для запроса: только назначаемые (базовая «Пользователь» серверу не нужна). */
function assignable(roles: UserRole[]): UserRole[] {
  return ASSIGNABLE_ROLES.filter((role) => roles.includes(role));
}
